/**
 * Logic for computing prop/state-derived table measurements (heights, paddings).
 *
 * These helpers are intentionally free of DOM access so they can be memoized in
 * selectors. DOM-dependent measurements remain in the component.
 */

import {INDENT_SIZE} from 'notebook/notes/lists/ListConstants';
import {
  DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT,
  FORMULA_BOX_HEIGHT_INLINE,
  MaximumTableHeight,
  MaximumTableHeightType,
  SCROLLBAR_WIDTH,
  TABLE_AXIS_CELL_HEIGHT,
  TABLE_HEADER_HEIGHT,
  TABLE_PADDING,
  TABLE_ROW_HEIGHT,
} from 'notebook/tables/constants/TableLayoutConstants';

/**
 * The horizontal padding (in px) applied to the right of the table when it is
 * rendered inline within a document (i.e. not maximized/split screen and a
 * document name is present).
 */
export const PADDING_RIGHT_SIZE = 35;

/**
 * The numeric maximum height for the table. When an explicit numeric
 * `maximumHeight` is provided it is used directly; otherwise the default fixed
 * height is used. This does NOT account for whether the table is maximized or
 * intended to stretch to full height.
 */
export function computeMaxTableHeight(maximumHeight: MaximumTableHeightType | undefined): number {
  return typeof maximumHeight === 'number' ? maximumHeight : DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT;
}

/**
 * The minimum height that the `<table>` tag must be. The table may be taller
 * than this, but it should never be shorter. For a fixed-height table this is
 * capped at `maxTableHeight`; for a full-height table there is no cap.
 */
export function computeMinTableHeight(
  rowCount: number,
  columnHeaderHeight: number,
  isFullHeight: boolean,
  maxTableHeight: number
): number {
  const minFullHeight = rowCount * TABLE_ROW_HEIGHT + columnHeaderHeight;
  const effectiveMaxTableHeight = !isFullHeight ? maxTableHeight : undefined;
  if (effectiveMaxTableHeight) {
    return Math.min(minFullHeight, effectiveMaxTableHeight);
  }
  return minFullHeight;
}

/**
 * The height of the title bar, when visible. Returns 0 when the title bar is
 * suppressed. For non-virtualized tables the formula box is rendered together
 * with the title bar as a floating element, but that combined height is
 * computed at the component level because it depends on DOM measurement.
 */
export function computeTitleBarHeight(suppressTitleBar: boolean): number {
  return suppressTitleBar ? 0 : TABLE_HEADER_HEIGHT;
}

/**
 * The left indentation (in px) applied to the table. Maximized/split-screen
 * tables are never indented; otherwise the indentation scales with the table's
 * nesting depth.
 */
export function computeIndentationPx(isMaximizedOrSplitScreen: boolean, indentation: number): number {
  return isMaximizedOrSplitScreen ? 0 : indentation * INDENT_SIZE;
}

/**
 * The right padding (in px) applied to the table. Only inline tables within a
 * named document receive this padding; maximized/split-screen tables and tables
 * outside of a document do not.
 */
export function computePaddingPx(isMaximizedOrSplitScreen: boolean, hasDocumentName: boolean): number {
  return isMaximizedOrSplitScreen || !hasDocumentName ? 0 : PADDING_RIGHT_SIZE;
}

/**
 * The height of the formula box, when visible. Returns 0 when the formula box
 * is not shown.
 *
 * The formula box height is measured from the DOM (via a `ResizeObserver` and
 * the periodic table measurement) and stored on state; this pure helper
 * encapsulates the visibility gating and the fallback to the inline constant
 * when no measurement is available yet (e.g. during initial render). The
 * measured value is passed in as `measuredHeightPx`.
 */
export function computeFormulaBarHeight(
  showFormulaBox: boolean,
  measuredHeightPx: number | undefined
): number {
  if (!showFormulaBox) {
    return 0;
  }
  return measuredHeightPx ?? FORMULA_BOX_HEIGHT_INLINE;
}

/**
 * The height of the sticky content that renders above the sticky column-header
 * row: the title bar plus the formula box. This does not include the sticky
 * header itself. The `formulaBarHeight` input is DOM-dependent and is measured
 * at the component level (see `computeFormulaBarHeight`).
 */
export function computeStickyTopContentHeight(titleBarHeight: number, formulaBarHeight: number): number {
  return titleBarHeight + formulaBarHeight;
}

/**
 * The current height of the table, given DOM-measured row heights.
 *
 * Returns `null` when the table should not have a computed fixed height
 * (maximized/split screen), `MaximumTableHeight.FULL` when the table should
 * expand to show all rows, `0` when the table is collapsed, or a numeric pixel
 * height when the table is fixed-height and its content exceeds the max.
 *
 * `rowHeights` are DOM-measured at the component level and passed in here. The
 * first entry is treated as the column-header row height. This function stays
 * pure so the height math can be unit tested independently of the DOM.
 *
 * `isFullHeightOverride` mirrors the original method's `isFullHeight` argument
 * (which forces a `FULL` result), while `isFullHeight` is the view's full-height
 * flag used only when capping the minimum table height.
 */
export function computeCurrentTableHeight(params: {
  isMaximizedOrSplitScreen: boolean;
  viewer: boolean;
  hasFullMaximumHeight: boolean;
  isFullHeightOverride: boolean;
  isCollapsed: boolean;
  isFullHeight: boolean;
  maxTableHeight: number;
  rowCount: number;
  rowHeights?: Array<number>;
  horizontalScrollbarVisible?: boolean;
}): MaximumTableHeightType | null {
  const {
    isMaximizedOrSplitScreen,
    viewer,
    hasFullMaximumHeight,
    isFullHeightOverride,
    isCollapsed,
    isFullHeight,
    maxTableHeight,
    rowCount,
    rowHeights,
    horizontalScrollbarVisible,
  } = params;

  if (isMaximizedOrSplitScreen) {
    return null;
  }
  if (viewer || hasFullMaximumHeight || isFullHeightOverride) {
    return MaximumTableHeight.FULL;
  }

  const tableColumnHeadersHeight =
    rowHeights && rowHeights.length > 0 && rowHeights[0] > 0
      ? Math.max(TABLE_AXIS_CELL_HEIGHT, rowHeights[0])
      : TABLE_AXIS_CELL_HEIGHT;
  const tableAreaTopAndBottomBorder = 2;
  const horizontalScrollbarHeight = horizontalScrollbarVisible ? SCROLLBAR_WIDTH : 0;

  if (isCollapsed) {
    return 0;
  }

  let tableHeight: number = tableAreaTopAndBottomBorder + horizontalScrollbarHeight;
  let heightOfAllRows: number = tableColumnHeadersHeight;
  // If the real row heights haven't been measured, then take a best guess using the default row height to
  // prevent the table from jittering while it first renders.
  if (rowHeights && rowHeights.length > 1 && rowHeights[0] > 0) {
    for (let i = 1; i < rowHeights.length; i++) {
      const height = rowHeights[i] ?? TABLE_ROW_HEIGHT;
      heightOfAllRows += height;
    }
  }
  // The height of the table should be at least as tall as the number of rows times the minimum row height
  tableHeight += Math.max(
    heightOfAllRows,
    computeMinTableHeight(rowCount, tableColumnHeadersHeight, isFullHeight, maxTableHeight)
  );

  if (tableHeight >= maxTableHeight) {
    return maxTableHeight;
  }
  return MaximumTableHeight.FULL;
}

/**
 * The maximum height of the table when it is fixed-height, or `undefined` when
 * the table is not fixed-height (maximized/split screen, explicitly full height,
 * an explicit `FULL` maximum, or viewer mode \u2014 all of which show every row).
 */
export function computeCurrentMaxTableHeight(params: {
  hasFullMaximumHeight: boolean;
  isMaximizedOrSplitScreen: boolean;
  isFullHeight: boolean;
  viewer: boolean;
  maxTableHeight: number;
}): number | undefined {
  const {hasFullMaximumHeight, isMaximizedOrSplitScreen, isFullHeight, viewer, maxTableHeight} = params;
  if (hasFullMaximumHeight || isMaximizedOrSplitScreen || isFullHeight || viewer) {
    return undefined;
  }
  return maxTableHeight;
}

/**
 * The width (in px) of the printable layout container. Indentation and padding
 * live inside the printable container's DOM node, so they are included here to
 * ensure proper scaling when printing. `tableWidth` is the (pure) sum of the
 * rendered column widths, computed by the caller from the table definition.
 */
export function computePrintableLayoutWidth(indentationPx: number, tableWidth: number): number {
  return indentationPx + tableWidth + TABLE_PADDING * 2;
}
